import { Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { CssBaseline, ThemeProvider, appTheme } from "@app/ui-kit";
import { AppShell } from "./components/AppShell";
import { AuthGate } from "./components/AuthGate";
import { LoadingScreen } from "./components/LoadingScreen";
import { AuthCallback } from "./routes/AuthCallback";
import { Resolver } from "./routes/Resolver";
import { ClientSelect } from "./routes/ClientSelect";
import { NoAccess } from "./routes/NoAccess";
import { UiKitGallery } from "./routes/UiKitGallery";
import { RequireAdmin } from "./admin/RequireAdmin";
import { ClientsList } from "./admin/ClientsList";
import { NewClient } from "./admin/NewClient";
import { ClientDetail } from "./admin/ClientDetail";
import { AppsIndex } from "./admin/apps/AppsIndex";
import { AppMaintenance } from "./admin/apps/AppMaintenance";
import { RedirectToTab } from "./admin/apps/RedirectToTab";
import { ClientPortalLayout } from "./client/ClientPortalLayout";
import { ClientHome } from "./client/ClientHome";
import { Documents } from "./client/Documents";
import { Team } from "./client/Team";
import { Activity } from "./client/settings/Activity";
import { ReferenceData } from "./client/ReferenceData";
import { SettingsLayout } from "./client/settings/SettingsLayout";
import { SettingsIndex } from "./client/settings/SettingsIndex";
import { RegionalFiscal } from "./client/settings/RegionalFiscal";
import { RedirectKeepingSearch } from "./client/settings/RedirectKeepingSearch";
import { AppRoute } from "./client/AppRoute";
import { ClientNotFound } from "./client/ClientNotFound";

const uiGalleryEnabled = import.meta.env.VITE_ENABLE_UI_GALLERY === "true";

export default function App() {
  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    const brandPrimary = rootStyles.getPropertyValue("--bkdp-deep-navy").trim();
    console.log("Host build info", {
      buildSha: import.meta.env.VITE_BUILD_SHA || "unset",
      buildTime: import.meta.env.VITE_BUILD_TIME || "unset",
      brandPrimary
    });
  }, []);

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/*"
          element={
            <AuthGate>
              <AppShell>
                <Suspense fallback={<LoadingScreen />}>
                  <Routes>
                    <Route path="/" element={<Resolver />} />
                    <Route path="/select" element={<ClientSelect />} />
                    <Route path="/no-access" element={<NoAccess />} />

                    <Route element={<RequireAdmin />}>
                      <Route path="/admin" element={<ClientsList />} />
                      <Route path="/admin/clients/new" element={<NewClient />} />
                      <Route path="/admin/clients/:clientId" element={<ClientDetail />} />
                      {/* MNT-3: what BKDP maintains for an application, rendered from the registry; nothing special-cased here. */}
                      <Route path="/admin/apps" element={<AppsIndex />} />
                      <Route path="/admin/apps/:slug" element={<AppMaintenance />} />
                      {/* MNT-4, MNT-2 criterion 4: the HIE-10 paths redirect to the tabs, search params and all; a portal admin with a bookmark is the person most likely to arrive here. */}
                      <Route path="/admin/due-diligence/library" element={<RedirectToTab slug="due-diligence" tab="library" />} />
                      <Route path="/admin/due-diligence/queue" element={<RedirectToTab slug="due-diligence" tab="queue" />} />
                    </Route>

                    <Route path="/c/:slug" element={<ClientPortalLayout />}>
                      <Route index element={<ClientHome />} />
                      <Route path="documents" element={<Documents />} />
                      {/* SET-1: one place for the things that configure this client. */}
                      <Route path="settings" element={<SettingsLayout />}>
                        <Route index element={<SettingsIndex />} />
                        <Route path="reference" element={<ReferenceData />} />
                        <Route path="regional" element={<RegionalFiscal />} />
                        <Route path="team" element={<Team />} />
                        <Route path="activity" element={<Activity />} />
                      </Route>
                      {/* The old addresses are in bookmarks and screenshots; they keep working, query string included. */}
                      <Route path="reference" element={<RedirectKeepingSearch to="settings/reference" />} />
                      <Route path="team" element={<RedirectKeepingSearch to="settings/team" />} />
                      <Route path="apps/:appSlug/*" element={<AppRoute />} />
                      {/* An unknown address under a client stays in that client's portal and says so; it never falls through to the shell's catch-all. */}
                      <Route path="*" element={<ClientNotFound />} />
                    </Route>

                    {uiGalleryEnabled && <Route path="/ui-kit" element={<UiKitGallery />} />}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </AppShell>
            </AuthGate>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}
